import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { usePermissions } from '@hooks/usePermissions';
import { Console } from '@utils';

import {
  currentOrderWithPrescriptions,
  CurrentOrderWithPrescriptionsQueryParams,
  CurrentOrderWithPrescriptionsQueryResponse,
} from '@queries/order/queries/currentOrderWithPrescriptions';
import { Product } from '@interfaces';
import { useAuth } from '@hooks/useAuth';
import { Header } from '@components/ModalHeader';
import { ModalButton } from '@components/ModalButton';
import { ModalContent } from '@components/ModalContent';
import { useModalRouter } from '@hooks/useModalRouter';
import { getRoute } from '@utils';
import { UploadImage } from './components/UploadImage';
import { Info } from './components/Info';

import { useSegment } from '@hooks/useSegment';

import { ContentContainer } from '@components/ContentContainer';
import { ItemsList } from './components/ItemsList';
import { Loader } from '@components/Loader';
import { usePendingPrescriptions } from '@hooks/usePendingPrescriptions';

interface PrescriptionsProps {
  redirectOnNonePending?: boolean;
  fromInsurance?: boolean;
}

export function Prescriptions({ redirectOnNonePending }: PrescriptionsProps) {
  const [loadingUser, setLoadingUser] = useState(true);
  const [emtpyError, setError] = useState(false);
  const { userCan } = usePermissions();
  const isLocalVendor = userCan('LOCAL_VENDOR');
  const router = useModalRouter();
  const { codigo: code } = router.query;
  const codeString = code ? decodeURI(code as string) : '';
  const { appUser } = useAuth({ forceAuth: true });
  const { track } = useSegment();

  const { data, loading, error } = useQuery<
    CurrentOrderWithPrescriptionsQueryResponse,
    CurrentOrderWithPrescriptionsQueryParams
  >(currentOrderWithPrescriptions(), {
    returnPartialData: true,
    fetchPolicy: 'cache-and-network',
    skip: !appUser,
  });
  const {
    requirePrescriptions,
    requirePrescriptionUpload,
    loading: prescriptionLoading,
    associatedPrescriptions,
  } = usePendingPrescriptions();

  const products = useMemo<Product[]>(() => {
    return data?.currentOrder?.orderDetails

      .filter(
        (detail) =>
          detail.product.prescriptionType &&
          detail.product.prescriptionType !== 'NOT_REQUIRED',
      )
      .map((detail) => detail.product);
  }, [data?.currentOrder?.orderDetails]);

  const productsByPrescriptionType = useMemo(() => {
    if (!isLocalVendor) return null;
    return data?.currentOrder?.orderDetails.reduce((acc, detail) => {
      const prescriptionType = detail?.product?.prescriptionType;
      if (prescriptionType) {
        const typeKey =
          prescriptionType == 'NOT_REQUIRED' ? 'SIMPLE' : prescriptionType;
        if (acc[typeKey]) {
          return {
            ...acc,
            [typeKey]: [
              ...acc[typeKey],
              {
                price: detail.price,
                quantity: detail.quantity,
                ...detail.product,
              },
            ],
          };
        }
        return {
          ...acc,
          [typeKey]: [
            {
              price: detail.price,
              quantity: detail.quantity,
              ...detail.product,
            },
          ],
        };
      }
      return acc;
    }, {} as Record<string, Product[]>);
  }, [data?.currentOrder?.orderDetails, isLocalVendor]);

  const prescriptionsUploaded = useMemo(() => {
    return data?.pendingPrescriptions?.length > 0;
  }, [data?.pendingPrescriptions?.length]);

  const handleNext = () => {
    if (isLocalVendor) {
      void router.push(getRoute('checkoutPaymentMethodLocal'));
    }
    if (!products) {
      void track('prescription press next without products', {
        products,
      });
      return;
    }
    if (
      !prescriptionsUploaded &&
      requirePrescriptionUpload &&
      associatedPrescriptions.length === 0
    ) {
      Console.log('No prescriptions uploaded');
      setError(true);
      return;
    }
    void track('confirm_prescriptions', {
      uploaded_prescription_amount: data?.pendingPrescriptions?.length,
    });
    void track('prescription upload', {
      uploaded_prescription_amount: data?.pendingPrescriptions?.length,
      products_amount: products?.length,
      prescription_types: Array.from(
        new Set(products.map((e) => e.prescriptionType)) ?? [],
      ),
    });
    void router.push(getRoute('checkoutPaymentConfirm'));
  };

  useEffect(() => {
    if (appUser) {
      setLoadingUser(false);
    }
  }, [appUser]);

  useEffect(
    function handleNoPrescriptionNeeded() {
      if (
        appUser &&
        !loading &&
        (!data?.currentOrder ||
          data?.currentOrder?.orderDetails?.length === 0) &&
        redirectOnNonePending
      ) {
        void router.replace(getRoute('cart'));
        return;
      }
      if (prescriptionLoading || loading || error || !products) return;
      void track('prescription request', {
        products_amount: products.length,
        prescription_types: Array.from(
          new Set(products.map((e) => e.prescriptionType)) ?? [],
        ),
        userId: appUser?.id,
        productIds: products.map((e) => e.id),
      });
      void track('prescription upload', {
        uploaded_prescription_amount: data?.pendingPrescriptions.length,
        products_amount: products.length,
        prescription_types: Array.from(
          new Set(products.map((e) => e.prescriptionType)) ?? [],
        ),
      });
      if (
        !loading &&
        !prescriptionLoading &&
        !requirePrescriptions &&
        redirectOnNonePending
      ) {
        setTimeout(() => {
          void router.replace(
            getRoute('checkout', code ? { codigo: codeString } : {}),
          );
        }, 300);
      }
    },
    [
      error,
      loading,
      router,
      track,
      products,
      data?.pendingPrescriptions?.length,
      data?.currentOrder,
      appUser,
      redirectOnNonePending,
      code,
      codeString,
      prescriptionLoading,
      requirePrescriptions,
    ],
  );

  return (
    <>
      <ContentContainer>
        <Header
          title={isLocalVendor ? 'Pedir receta' : 'Subir receta'}
          backIcon
          backFunction={() => {
            void router.push(getRoute('checkoutPaymentMethod'));
          }}
        />
        {isLocalVendor ? (
          <ModalContent>
            <ItemsList
              productsByPrescription={Object.entries(
                productsByPrescriptionType ?? {},
              ).map(([type, products]) => {
                return [type, products];
              })}
            />
          </ModalContent>
        ) : (
          <ModalContent>
            {loadingUser ? (
              <Loader />
            ) : (
              <>
                {data && (
                  <UploadImage
                    emptyError={emtpyError}
                    setError={setError}
                    data={data}
                  />
                )}

                <Info />
              </>
            )}
          </ModalContent>
        )}
        <ModalButton handleNext={handleNext} text={'Siguiente'} />
      </ContentContainer>
    </>
  );
}
